import React, { useState } from 'react';
import Chart from 'react-apexcharts';
import { FiMail } from 'react-icons/fi';

interface Campaign {
  id: string;
  name: string;
  type: string;
  status: string;
  recipients: string;
  openRate: string;
  clickRate: string;
  triggerDate: string;
  action: string;
  subtitle?: string;
  segment?: string;
  tags?: string[];
  deliveredDate?: string;
  stats?: {
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    bounced: number;
    spamReported: number;
    openRate: string;
    clickRate: string;
    newSubscribers: number;
    unsubscribes: number;
  };
  lastOpened?: { name: string; time: string };
  lastClicked?: { name: string; time: string };
}

const FiMailComponent = FiMail as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// Renders a stat box for campaign metrics or subscriber activity
const StatBox: React.FC<{ label: string; value: string | number }> = ({
  label,
  value,
}) =>
  label !== 'New Subscribers' &&
  label !== 'Unsubscribes' &&
  label !== 'Last Opened' &&
  label !== 'Last Clicked' ? (
    <div className='bg-white rounded-md p-3 flex items-center justify-between border border-gray-200 shadow-sm'>
      <div>
        <h3 className='text-[#657584] text-[14px] mb-2 font-semibold'>
          {label}
        </h3>
        <p className='text-[28px] text-[#3F5265] font-semibold'>{value}</p>
      </div>
      <div style={{ height: '-webkit-fill-available' }}>
        <span className='flex items-center justify-center mr-2 w-[30px] h-[30px] rounded-[5px] bg-[#FFF1EC]'>
          <FiMailComponent className='text-orange-500 text-2xl' size={17} />
        </span>
      </div>
    </div>
  ) : (
    <div
      className={`bg-white ${
        label === 'New Subscribers' ? 'rounded-t' : 'rounded-b'
      } p-[13.5px] flex items-center justify-between border border-gray-200 shadow-sm`}
    >
      <div className='flex justify-between items-center w-[100%]'>
        <h3 className='text-[#18181B] text-[14px] font-semibold'>{label}</h3>
        <p className='text-[20px] text-[#3F5265] font-semibold'>{value}</p>
      </div>
    </div>
  );

interface OverviewProps {
  campaign: any | Campaign;
}

const ReportsCampaignOverview: React.FC<OverviewProps> = ({ campaign }) => {
  // State for filtering engagement chart data (24 hours or 7 days)
  const [filter, setFilter] = useState<'24h' | '7d'>('24h');

  // Handle missing campaign stats
  if (!campaign.stats) {
    return (
      <div className='bg-white p-4 rounded-lg shadow border'>
        <h3 className='text-[20px] font-semibold text-[#262626] mb-2'>
          Engagement
        </h3>
        <p className='text-gray-500 text-sm'>No stats available yet.</p>
      </div>
    );
  }

  // Generates engagement data for the last 24 hours
  const engagementData24h = Array.from({ length: 24 }, (_, i) => ({
    hour: `${i.toString().padStart(2, '0')}:00`,
    open: Math.floor(campaign.stats!.opened / 24 + Math.random() * 10),
    click: Math.floor(campaign.stats!.clicked / 24 + Math.random() * 5),
  }));

  // Generates engagement data for the last 7 days
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const engagementData7d = days.map((day) => ({
    day,
    open: Math.floor(campaign.stats!.opened / 7 + Math.random() * 20),
    click: Math.floor(campaign.stats!.clicked / 7 + Math.random() * 10),
  }));

  // Selects engagement data based on filter
  const engagementData =
    filter === '24h' ? engagementData24h : engagementData7d;

  // Configures chart series for ApexCharts
  const series =
    filter === '24h'
      ? [
          { name: 'Open', data: engagementData.map((d) => d.open) },
          { name: 'Click', data: engagementData.map((d) => d.click) },
        ]
      : [
          { name: 'Open', data: engagementData.map((d) => d.open) },
          { name: 'Click', data: engagementData.map((d) => d.click) },
        ];

  // Configures chart options for ApexCharts
  const options: ApexCharts.ApexOptions = {
    chart: { type: 'line', toolbar: { show: false } },
    stroke: { curve: 'smooth', width: 3 },
    xaxis: {
      categories: engagementData.map((d) =>
        filter === '24h'
          ? (d as { hour: string }).hour
          : (d as { day: string }).day
      ),
      labels: { style: { fontSize: '12px', colors: '#657584' } },
    },
    yaxis: { labels: { style: { fontSize: '12px', colors: '#657584' } } },
    colors: ['#FF5733', '#008FFB'],
    legend: { position: 'top', horizontalAlign: 'left' },
    markers: { size: 4 },
    grid: { borderColor: '#f1f1f1' },
  };

  return (
    <>
      {/* Stats */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-6'>
        <StatBox label='Sent' value={campaign.stats.sent} />
        <StatBox label='Delivered' value={campaign.stats.delivered} />
        <StatBox label='Opened' value={campaign.stats.opened} />
        <StatBox label='Clicked' value={campaign.stats.clicked} />
        <StatBox label='Bounced' value={campaign.stats.bounced} />
        <StatBox label='Spam Reported' value={campaign.stats.spamReported} />
        <StatBox label='Open Rate' value={campaign.stats.openRate} />
        <StatBox label='Click Rate' value={campaign.stats.clickRate} />
      </div>

      {/* Subscribers + Activity */}
      <div className='grid grid-cols-2 md:grid-cols-2 gap-4 mb-6'>
        <div>
          <StatBox
            label='New Subscribers'
            value={campaign.stats.newSubscribers}
          />
          <StatBox label='Unsubscribes' value={campaign.stats.unsubscribes} />
        </div>
        <div>
          <div className='bg-white p-[10px] justify-between flex items-center rounded-t border border-gray-200 shadow-sm'>
            <p className='text-[#18181B] text-[14px] font-semibold'>
              Last Opened
            </p>
            <div className='flex items-center gap-2'>
              <img
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                  campaign.lastOpened.name
                )}&background=random`}
                alt={campaign.lastOpened.name}
                className='w-8 h-8 rounded-full'
              />
              <div>
                <p className='font-medium text-[#18181B] text-[14px]'>
                  {campaign.lastOpened.name}
                </p>
                <p className='text-[#657584] text-[12px] text-xs'>
                  {campaign.lastOpened.time}
                </p>
              </div>
            </div>
          </div>
          <div className='bg-white p-[10px] justify-between flex items-center rounded-b border border-gray-200 shadow-sm'>
            <p className='text-[#18181B] text-[14px] font-semibold'>
              Last Clicked
            </p>
            <div className='flex items-center gap-2'>
              <img
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                  campaign.lastClicked.name
                )}&background=random`}
                alt={campaign.lastClicked.name}
                className='w-8 h-8 rounded-full'
              />
              <div>
                <p className='font-medium text-[#18181B] text-[14px]'>
                  {campaign.lastClicked.name}
                </p>
                <p className='text-[#657584] text-[12px] text-xs'>
                  {campaign.lastClicked.time}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Engagement Chart */}
      <div className='bg-white p-4 rounded-lg shadow border'>
        <div className='flex justify-between items-center mb-4'>
          <h3 className='text-lg font-semibold text-gray-800'>Engagement</h3>
          <select
            className='border rounded p-2 text-[14px] border-[#E1EAF0] bg-[#F5F8FA] text-[#3F5265]'
            value={filter}
            onChange={(e) => setFilter(e.target.value as '24h' | '7d')}
          >
            <option value='24h'>Last 24 Hours</option>
            <option value='7d'>Last 7 Days</option>
          </select>
        </div>
        <Chart options={options} series={series} type='line' height={300} />
      </div>
    </>
  );
};

// Export the ReportsCampaignOverview component as the default export
export default ReportsCampaignOverview;
